import axios from 'axios';
import {formatDate} from "../DateFormatter.js";

const apiUrl = process.env.VUE_APP_API_URL;

// const api = axios.create({
//     baseURL: '/api', // esto se redirige por el proxy
//     withCredentials: true, // si usas cookies
// });

/**
 * Authenticates a user with provided email and password. On successful login, user data and token are stored in sessionStorage.
 *
 * @param {string} email - The email address of the user attempting to log in.
 * @param {string} password - The password of the user attempting to log in.
 * @return {Promise<Object>} Returns a promise resolving to an object containing user details and a token on successful login.
 * @throws Will throw an error if the login request fails or returns an unsuccessful response.
 */
export async function loginService(email, password) {

    try {

        const response = await axios.post( `${apiUrl}/login`, {
        // const response = await api.post( `${apiUrl}/login`, {
            email,
            password,
        });

        if (!response.data.ok) {
            throw new Error(response.data.error || 'Unknown server error');
        }

        /**
         * Represents the unique identifier for a user.
         * This variable is commonly used to reference or distinguish a user
         * in a database or application.
         *
         * @type {number|string}
         */
        const {id_user, email: userEmail, name: name, r_date, b_date: birthDate, role} = response.data.user;
        /**
         * A string representing a unique identifier often used for authentication
         * or session management purposes. Typically, this value is generated
         * dynamically and is designed to provide a secure means of verifying
         * identity or access rights in an application.
         */
        const {token, ok, message} = response.data;

        /**
         * Represents user information.
         *
         * @typedef {Object} userData
         * @property {boolean} ok Indicates if the operation was successful.
         * @property {number} id_user The unique identifier for the user.
         * @property {string} email The email address of the user.
         * @property {string} name The name of the user.
         * @property {string} r_date The registration date of the user in ISO format.
         * @property {string} b_date The birth date of the user.
         * @property {string} role The role assigned to the user.
         * @property {string} message A message associated with the user or operation.
         * @property {string} token Authentication token for the user.
         */
        const userData = {
            ok,
            id_user,
            email: userEmail,
            name: name,
            r_date,
            b_date: birthDate,
            role,
            message,
            token,
        };

        /*
        * Store user data in sessionStorage for later use.
        */
        sessionStorage.setItem('ok', userData.ok);
        sessionStorage.setItem('id', userData.id_user);
        sessionStorage.setItem('email', userData.email);
        sessionStorage.setItem('name', userData.name);
        sessionStorage.setItem('r_date', formatDate(userData.r_date));
        sessionStorage.setItem('b_date', userData.b_date ? formatDate(userData.b_date) : null);
        sessionStorage.setItem('role', userData.role);
        sessionStorage.setItem('message', userData.message);
        sessionStorage.setItem('token', userData.token);

        return userData;

    } catch (error) {
        console.error('Error al iniciar sesión:', error.response?.data?.message || error.message);
        throw error; // Opcional: vuelve a lanzar el error si necesitas manejarlo en otro lugar
    }
}

/**
 * Logs out the user by clearing all data stored in the session storage.
 *
 * @return {void} Does not return any value.
 */
export function logout() {
    sessionStorage.clear();
}

/**
 * Checks if user data is stored in the session storage.
 *
 * This function verifies the presence of a specific session key
 * to determine if the user data exists in the session storage.
 *
 * @return {boolean} Returns true if the session key exists and contains data, otherwise false.
 */
export function checkUserData() {
    return !!sessionStorage.getItem('token');
}
